import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, LogOut, PackageCheck, RotateCcw } from "lucide-react";
import { Header } from "@/components/Header";
import { CartSheet } from "@/components/CartSheet";
import { useAuth, authHeader } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Account() {
  const { user, loading, logout } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/account" } }); return; }
    axios.get(`${API}/auth/orders`, { headers: authHeader() })
      .then((r) => setOrders(r.data))
      .catch(() => {})
      .finally(() => setFetching(false));
  }, [user, loading, navigate]);

  const reorder = async (items) => {
    let added = 0;
    let outOfStock = 0;
    for (const it of items) {
      try {
        const r = await axios.get(`${API}/products/${it.product_id}`);
        const p = r.data;
        if (!p || p.stock <= 0) { outOfStock += 1; continue; }
        const qty = Math.min(it.quantity, p.stock);
        addItem(p, qty);
        added += 1;
      } catch {
        outOfStock += 1;
      }
    }
    if (added > 0) toast.success(`${added} item${added === 1 ? "" : "s"} added to cart${outOfStock ? ` · ${outOfStock} unavailable` : ""}`);
    else toast.error("Items are no longer available");
  };

  const doLogout = () => {
    logout();
    toast.success("Signed out");
    navigate("/");
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="py-24 text-center text-muted-foreground text-sm uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <CartSheet />
      <div className="max-w-4xl mx-auto px-6 py-14" data-testid="account-page">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary">Member</p>
            <h1 className="font-display font-bold text-4xl tracking-tight mt-3">{user.name || user.email}<span className="text-primary">.</span></h1>
            <p className="text-sm text-muted-foreground mt-2" data-testid="account-email">{user.email}</p>
          </div>
          <button
            data-testid="account-logout"
            onClick={doLogout}
            className="inline-flex items-center gap-2 border border-border px-4 py-2 text-xs uppercase tracking-wider hover:border-foreground transition-colors"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>

        <div className="mt-10">
          <h2 className="font-display font-bold text-2xl tracking-tight">ORDER HISTORY</h2>
          {fetching ? (
            <div className="py-16 text-center text-muted-foreground text-sm uppercase tracking-widest">Loading orders...</div>
          ) : orders.length === 0 ? (
            <div data-testid="no-orders-message" className="mt-6 border border-border p-10 text-center">
              <PackageCheck className="mx-auto text-muted-foreground" size={28} />
              <p className="font-display font-semibold text-lg mt-4">No orders yet.</p>
              <p className="text-sm text-muted-foreground mt-2">Your paid orders will show up here for easy reordering.</p>
              <Link to="/shop" className="inline-block mt-6 bg-primary text-white px-6 py-3 text-sm uppercase tracking-wider hover:bg-foreground transition-colors">Shop Now</Link>
            </div>
          ) : (
            <div className="mt-6 space-y-4">
              {orders.map((o) => (
                <div key={o.session_id} data-testid={`account-order-${o.session_id}`} className="border border-border">
                  <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border bg-secondary/50">
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</span>
                      <span className="font-display font-semibold">${((o.amount_total ?? o.amount) / 100).toFixed(2)}</span>
                      <span className={`text-xs uppercase tracking-wider px-2 py-1 ${o.fulfillment_status === "fulfilled" ? "bg-foreground text-white" : "bg-primary/10 text-primary"}`}>
                        {o.fulfillment_status === "fulfilled" ? "Shipped" : "Processing"}
                      </span>
                    </div>
                    <button
                      data-testid={`reorder-button-${o.session_id}`}
                      onClick={() => reorder(o.items || [])}
                      className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-foreground transition-colors"
                    >
                      <RotateCcw size={14} /> Reorder
                    </button>
                  </div>
                  <div className="p-4 space-y-1 text-sm">
                    {(o.items || []).map((i, idx) => (
                      <div key={idx} className="flex justify-between">
                        <span>{i.name} ×{i.quantity}</span>
                        <span className="text-muted-foreground">${(i.unit_price * i.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
